import React, { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { CheckCircle, XCircle, AlertTriangle, FileText } from 'lucide-react'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const NAVY  = '#0F1F3D'
const GREEN = '#2A9D5C'

function fmt(n) {
  return parseFloat(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(d) {
  if (!d) return 'TBD'
  return new Date(d + 'T12:00').toLocaleDateString('en-CA', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  })
}

// ─── PublicQuoteAcceptPage ────────────────────────────────────────────────────
// Accessible at /quote-accept/:token?response=accept|decline
// No auth required — uses acceptance_token to look up the quote.

export default function PublicQuoteAcceptPage() {
  const { token } = useParams()
  const [searchParams] = useSearchParams()
  const intentFromUrl = searchParams.get('response') // 'accept' | 'decline' | null

  const [phase,  setPhase]  = useState('loading')   // loading | review | done | error
  const [quote,  setQuote]  = useState(null)
  const [client, setClient] = useState(null)
  const [action, setAction] = useState(null)         // 'accepted' | 'declined'
  const [busy,   setBusy]   = useState(false)
  const [err,    setErr]    = useState('')

  // ── Load quote by token ──────────────────────────────────────────────────

  useEffect(() => {
    async function load() {
      if (!token) { setErr('Invalid link — no token found.'); setPhase('error'); return }

      const { data: q, error: qErr } = await supabase
        .from('quotes')
        .select('*, clients(id, name, email, phone)')
        .eq('acceptance_token', token)
        .single()

      if (qErr || !q) {
        setErr('This quote link is invalid or has expired.')
        setPhase('error')
        return
      }

      setQuote(q)
      setClient(q.clients)

      // If the quote is already resolved, jump straight to done
      if (q.status === 'accepted' || q.status === 'declined' || q.status === 'converted') {
        setAction(q.status === 'accepted' || q.status === 'converted' ? 'accepted' : 'declined')
        setPhase('done')
        return
      }

      // If a response intent came in via URL query param, process immediately
      if (intentFromUrl === 'accept' || intentFromUrl === 'decline') {
        setPhase('confirming')
        await processResponse(q, intentFromUrl === 'accept' ? 'accepted' : 'declined')
      } else {
        setPhase('review')
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // ── Process the accept/decline action ───────────────────────────────────

  async function processResponse(quoteRecord, newStatus) {
    setBusy(true)
    setErr('')
    const update = { status: newStatus }
    if (newStatus === 'accepted') update.accepted_at = new Date().toISOString()

    const { error: upErr } = await supabase
      .from('quotes')
      .update(update)
      .eq('id', quoteRecord.id)

    if (upErr) {
      setErr('Something went wrong recording your response. Please try again or contact us directly.')
      setPhase('review')
      setBusy(false)
      return
    }

    setAction(newStatus)
    setPhase('done')
    setBusy(false)
  }

  function handleAction(act) {
    if (!quote) return
    processResponse(quote, act)
  }

  // ── Render phases ────────────────────────────────────────────────────────

  const pageStyle = {
    minHeight: '100vh',
    background: '#f0f2f5',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '40px 16px',
    fontFamily: 'Arial, Helvetica, sans-serif',
  }

  const cardStyle = {
    background: 'white',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,.12)',
    width: '100%',
    maxWidth: 560,
  }

  const headerStyle = {
    background: NAVY,
    padding: '28px 32px',
    color: 'white',
  }

  const bodyStyle = {
    padding: '32px',
  }

  if (phase === 'loading' || phase === 'confirming') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={headerStyle}>
            <div style={{ fontWeight: 700, fontSize: 20 }}>Winnipeg Tent Rental</div>
            <div style={{ opacity: 0.65, fontSize: 13, marginTop: 4 }}>Professional Tent &amp; Event Equipment</div>
          </div>
          <div style={{ ...bodyStyle, textAlign: 'center', padding: '60px 32px' }}>
            <div style={{ color: '#6b7280', fontSize: 15 }}>Loading your quote…</div>
          </div>
        </div>
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={headerStyle}>
            <div style={{ fontWeight: 700, fontSize: 20 }}>Winnipeg Tent Rental</div>
            <div style={{ opacity: 0.65, fontSize: 13, marginTop: 4 }}>Professional Tent &amp; Event Equipment</div>
          </div>
          <div style={{ ...bodyStyle, textAlign: 'center' }}>
            <AlertTriangle size={40} style={{ color: '#EF4444', marginBottom: 16 }} />
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 10, color: NAVY }}>Link Not Found</div>
            <div style={{ color: '#6b7280', lineHeight: 1.6 }}>{err || 'This quote link is invalid or has expired.'}</div>
            <div style={{ marginTop: 20, fontSize: 14, color: '#6b7280' }}>
              Please contact us at{' '}
              <a href="mailto:info@winnipegtentrentals.ca" style={{ color: GREEN }}>info@winnipegtentrentals.ca</a>
              {' '}if you need assistance.
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (phase === 'done') {
    const accepted = action === 'accepted'
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ ...headerStyle, background: accepted ? GREEN : '#4B5563' }}>
            <div style={{ fontWeight: 700, fontSize: 20 }}>Winnipeg Tent Rental</div>
            <div style={{ opacity: 0.8, fontSize: 13, marginTop: 4 }}>Professional Tent &amp; Event Equipment</div>
          </div>
          <div style={{ ...bodyStyle, textAlign: 'center' }}>
            {accepted ? (
              <CheckCircle size={48} style={{ color: GREEN, marginBottom: 20 }} />
            ) : (
              <XCircle size={48} style={{ color: '#6B7280', marginBottom: 20 }} />
            )}
            <div style={{ fontWeight: 700, fontSize: 22, color: NAVY, marginBottom: 12 }}>
              {accepted ? 'Quote Accepted!' : 'Quote Declined'}
            </div>
            {accepted ? (
              <>
                <div style={{ color: '#374151', fontSize: 15, lineHeight: 1.7, maxWidth: 400, margin: '0 auto' }}>
                  Thank you, <strong>{client?.name || 'valued client'}</strong>! We've received your acceptance for quote{' '}
                  <strong>{quote?.quote_number}</strong>.
                </div>
                <div style={{
                  background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8,
                  padding: '16px 20px', margin: '20px auto', maxWidth: 360, textAlign: 'left',
                  fontSize: 14, color: '#15803D', lineHeight: 1.6,
                }}>
                  <strong>What happens next:</strong><br />
                  Our team will be in touch shortly to arrange your deposit and finalize the booking details.
                </div>
              </>
            ) : (
              <div style={{ color: '#6b7280', fontSize: 15, lineHeight: 1.7, maxWidth: 400, margin: '0 auto' }}>
                We've recorded your response for quote <strong>{quote?.quote_number}</strong>.
                If you change your mind or have questions, please reach out to us at{' '}
                <a href="mailto:info@winnipegtentrentals.ca" style={{ color: GREEN }}>info@winnipegtentrentals.ca</a>.
              </div>
            )}
          </div>
          <div style={{
            background: '#F9FAFB', padding: '16px 32px', borderTop: '1px solid #E5E7EB',
            textAlign: 'center', fontSize: 12, color: '#9CA3AF',
          }}>
            Winnipeg Tent Rental · Winnipeg, MB · info@winnipegtentrentals.ca
          </div>
        </div>
      </div>
    )
  }

  // phase === 'review' — show the quote summary with Accept / Decline buttons

  const lineItems = quote?.quote_line_items || []

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ fontWeight: 700, fontSize: 20 }}>Winnipeg Tent Rental</div>
          <div style={{ opacity: 0.65, fontSize: 13, marginTop: 4 }}>Professional Tent &amp; Event Equipment</div>
        </div>

        <div style={bodyStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <FileText size={28} style={{ color: NAVY, flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 18, color: NAVY }}>
                Your Quote — {quote?.quote_number}
              </div>
              <div style={{ fontSize: 14, color: '#6b7280', marginTop: 2 }}>
                Hi {client?.name || 'there'}, please review your quote below.
              </div>
            </div>
          </div>

          {/* Event details */}
          <div style={{
            background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8,
            padding: '16px 20px', marginBottom: 20, fontSize: 14,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
              {quote?.event_date && (
                <div>
                  <div style={{ fontWeight: 600, color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 2 }}>Event Date</div>
                  <div style={{ color: NAVY, fontWeight: 600 }}>{fmtDate(quote.event_date)}</div>
                </div>
              )}
              {quote?.event_type && (
                <div>
                  <div style={{ fontWeight: 600, color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 2 }}>Event Type</div>
                  <div style={{ color: NAVY }}>{quote.event_type.charAt(0).toUpperCase() + quote.event_type.slice(1)}</div>
                </div>
              )}
              {quote?.address && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontWeight: 600, color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 2 }}>Location</div>
                  <div style={{ color: NAVY }}>{quote.address}</div>
                </div>
              )}
            </div>
          </div>

          {/* Totals summary */}
          <div style={{
            background: NAVY, color: 'white', borderRadius: 8,
            padding: '20px 24px', marginBottom: 24,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, opacity: 0.8 }}>
              <span>Subtotal</span><span>${fmt(quote?.subtotal)}</span>
            </div>
            {quote?.discount_amount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, opacity: 0.8, color: '#6EE7B7' }}>
                <span>Discount</span><span>−${fmt(quote.discount_amount)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, opacity: 0.8 }}>
              <span>GST (5%)</span><span>${fmt(quote?.gst)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 14, opacity: 0.8 }}>
              <span>PST (7%)</span><span>${fmt(quote?.pst)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,.25)', paddingTop: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 17 }}>Total</span>
              <span style={{ fontWeight: 800, fontSize: 22, color: '#6EE7B7' }}>${fmt(quote?.grand_total)}</span>
            </div>
            {quote?.deposit_required > 0 && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,.15)', fontSize: 13, opacity: 0.75 }}>
                Deposit required to book: ${fmt(quote.deposit_required)}
              </div>
            )}
          </div>

          {err && (
            <div style={{
              background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6,
              padding: '12px 16px', marginBottom: 16, fontSize: 14, color: '#B91C1C',
              display: 'flex', gap: 8, alignItems: 'flex-start',
            }}>
              <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              {err}
            </div>
          )}

          {/* CTA buttons */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <button
              onClick={() => handleAction('accepted')}
              disabled={busy}
              style={{
                flex: 1, padding: '14px', borderRadius: 8, border: 'none',
                background: GREEN, color: 'white', fontWeight: 700,
                fontSize: 16, cursor: busy ? 'not-allowed' : 'pointer',
                opacity: busy ? 0.7 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                fontFamily: 'Arial, sans-serif',
              }}
            >
              <CheckCircle size={18} /> Accept Quote
            </button>
            <button
              onClick={() => handleAction('declined')}
              disabled={busy}
              style={{
                flex: 1, padding: '14px', borderRadius: 8,
                border: '1px solid #D1D5DB', background: 'white', color: '#374151',
                fontWeight: 600, fontSize: 15, cursor: busy ? 'not-allowed' : 'pointer',
                opacity: busy ? 0.7 : 1,
                fontFamily: 'Arial, sans-serif',
              }}
            >
              Decline
            </button>
          </div>

          <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', lineHeight: 1.6 }}>
            Questions? Contact us at{' '}
            <a href="mailto:info@winnipegtentrentals.ca" style={{ color: GREEN }}>
              info@winnipegtentrentals.ca
            </a>
          </div>
        </div>

        <div style={{
          background: '#F9FAFB', padding: '14px 32px', borderTop: '1px solid #E5E7EB',
          textAlign: 'center', fontSize: 12, color: '#9CA3AF',
        }}>
          Winnipeg Tent Rental · Winnipeg, MB
        </div>
      </div>
    </div>
  )
}
